import React, { useEffect, useMemo, useState } from "react";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Tooltip } from "primereact/tooltip";
import { Tag } from "primereact/tag";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import {
  useGetSupportCategoriesQuery,
  useCreateSupportCategoryMutation,
  useUpdateSupportCategoryMutation,
  useReorderSupportCategoriesMutation,
  useDeleteSupportCategoryMutation,
} from "../../store/services/support";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useTenantScope } from "../../hooks/useTenantScope";

import { SupportCategory } from "../../models/support";
import { formatSupportDateTime } from "../../utils/functions/parseInstant";
import {
  SUPPORT_ICON_OPTIONS,
  getSupportIconOption,
} from "../../utils/constants/supportIcons";
import { Dropdown } from "primereact/dropdown";

const getApiError = (e: any): string =>
  e?.data?.error?.message || "Ocurrió un error";

interface CategoryFormValues {
  name: string;
  icon: string;
  description: string;
  tenant_id: string;
}

const categoryValidationSchema = Yup.object().shape({
  name: Yup.string().trim().required("El nombre es requerido"),
  icon: Yup.string(),
  description: Yup.string().trim().max(200, "Máximo 200 caracteres"),
});

export function SupportCategories() {
  const NO_TENANT_LABEL = 'Sin tenant'
  const NO_TENANT_VALUE = '__NO_TENANT__'
  const { data: categories, isLoading, isFetching } = useGetSupportCategoriesQuery();
  const [createCategory, { isLoading: isCreating }] = useCreateSupportCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateSupportCategoryMutation();
  const [reorderCategories, { isLoading: isReordering }] = useReorderSupportCategoriesMutation();
  const [deleteCategory, { isLoading: isDeleting }] = useDeleteSupportCategoryMutation();
  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();

  const [orderedCategories, setOrderedCategories] = useState<SupportCategory[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [categoryToEdit, setCategoryToEdit] = useState<SupportCategory | undefined>(undefined);
  const [categoryToDelete, setCategoryToDelete] = useState<SupportCategory | undefined>(undefined);
  const [categoryToView, setCategoryToView] = useState<SupportCategory | undefined>(undefined);
  const [togglingId, setTogglingId] = useState<string | undefined>(undefined);

  useEffect(() => {
    setOrderedCategories(Array.isArray(categories) ? categories : []);
  }, [categories]);

  const isEdit = !!categoryToEdit;

  const tenantOptions = useMemo(
    () => [
      { id: NO_TENANT_VALUE, displayName: NO_TENANT_LABEL },
      ...(Array.isArray(tenants)
        ? tenants.map((tenant) => ({
          ...tenant,
          displayName: `${tenant.provider || tenant.id} ${tenant.emailDomain ? `(${tenant.emailDomain})` : ""
            }`.trim(),
        }))
        : []),
    ],
    [tenants]
  );

  const formik = useFormik<CategoryFormValues>({
    initialValues: {
      name: categoryToEdit?.name || "",
      icon: categoryToEdit?.icon || "",
      description: categoryToEdit?.description || "",
      tenant_id: NO_TENANT_VALUE,
    },
    enableReinitialize: true,
    validationSchema: categoryValidationSchema,
    onSubmit: (values) => {
      const name = values.name.trim();
      const icon = values.icon.trim();
      const description = values.description.trim();

      if (categoryToEdit) {
        updateCategory({ id: categoryToEdit.id, name, icon, description })
          .unwrap()
          .then(() => {
            toast.success("Categoría actualizada");
            hideDialog();
          })
          .catch((e) => toast.error(getApiError(e)));
      } else {
        createCategory({
          name,
          ...(icon ? { icon } : {}),
          ...(description ? { description } : {}),
          tenant_id: values.tenant_id === NO_TENANT_VALUE ? null : values.tenant_id || null,
        })
          .unwrap()
          .then(() => {
            toast.success("Categoría creada");
            hideDialog();
          })
          .catch((e) => toast.error(getApiError(e)));
      }
    },
  });

  const { setFieldValue: setCategoryFieldValue } = formik;
  const selectedTenantId = formik.values.tenant_id;

  useEffect(() => {
    if (isEdit) return;
    if (
      tenantScope.locked &&
      tenantScope.tenantId &&
      selectedTenantId !== tenantScope.tenantId
    ) {
      setCategoryFieldValue("tenant_id", tenantScope.tenantId);
    }
  }, [isEdit, tenantScope.locked, tenantScope.tenantId, selectedTenantId, setCategoryFieldValue]);

  const openNewDialog = () => {
    setCategoryToEdit(undefined);
    setDialogVisible(true);
  };

  const openEditDialog = (category: SupportCategory) => {
    setCategoryToEdit(category);
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setCategoryToEdit(undefined);
    formik.resetForm();
  };

  const handleRowReorder = (e: DataTableRowReorderEvent<SupportCategory[]>) => {
    const reordered = e.value;
    setOrderedCategories(reordered);
    reorderCategories({ category_ids: reordered.map((c) => c.id) })
      .unwrap()
      .then(() => toast.success("Orden de categorías actualizado"))
      .catch((err) => {
        toast.error(getApiError(err));
        setOrderedCategories(Array.isArray(categories) ? categories : []);
      });
  };

  const handleToggleActive = (category: SupportCategory, isActive: boolean) => {
    setTogglingId(category.id);
    updateCategory({ id: category.id, is_active: isActive })
      .unwrap()
      .then(() => {
        toast.success(isActive ? "Categoría activada" : "Categoría desactivada");
      })
      .catch((e) => toast.error(getApiError(e)))
      .finally(() => setTogglingId(undefined));
  };

  const handleDelete = () => {
    if (!categoryToDelete) return;
    deleteCategory(categoryToDelete.id)
      .unwrap()
      .then((res) => {
        toast.success(`Categoría eliminada (${res.deleted_articles} artículos)`);
      })
      .catch((e) => toast.error(getApiError(e)))
      .finally(() => setCategoryToDelete(undefined));
  };

  const formatDateTime = (value: string): string => formatSupportDateTime(value);

  const iconBodyTemplate = (category: SupportCategory) => {
    const option = getSupportIconOption(category.icon);
    if (option) {
      return (
        <span className="flex align-items-center gap-2">
          <i className={`${option.primeIcon} text-primary`} />
          <span className="text-600">{option.label}</span>
        </span>
      );
    }
    return category.icon || "-";
  };

  // La descripción puede ser larga: se corta con ellipsis y el texto completo va al tooltip.
  const descriptionBodyTemplate = (category: SupportCategory) => {
    const description = category.description?.trim();
    if (!description) return <span className="text-500">—</span>;
    return (
      <span
        className="support-category-description block overflow-hidden text-overflow-ellipsis white-space-nowrap"
        style={{ maxWidth: "20rem" }}
        data-pr-tooltip={description}
        data-pr-position="top"
      >
        {description}
      </span>
    );
  };

  const activeBodyTemplate = (category: SupportCategory) => (
    <InputSwitch
      checked={category.isActive}
      disabled={isUpdating && togglingId === category.id}
      onChange={(e: InputSwitchChangeEvent) =>
        handleToggleActive(category, !!e.value)
      }
    />
  );

  const actionsBodyTemplate = (category: SupportCategory) => (
    <div className="flex gap-2">
      <Button
        type="button"
        icon="pi pi-eye"
        rounded
        outlined
        tooltip="Ver detalle"
        tooltipOptions={{ position: "top" }}
        onClick={() => setCategoryToView(category)}
      />
      <Button
        type="button"
        icon="pi pi-pencil"
        rounded
        outlined
        severity="info"
        tooltip="Editar"
        tooltipOptions={{ position: "top" }}
        onClick={() => openEditDialog(category)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        rounded
        outlined
        severity="danger"
        tooltip="Eliminar"
        tooltipOptions={{ position: "top" }}
        onClick={() => setCategoryToDelete(category)}
      />
    </div>
  );

  const dialogFooter = (
    <div>
      <Button
        type="button"
        label="Cancelar"
        icon="pi pi-times"
        severity="secondary"
        onClick={hideDialog}
        disabled={isCreating || isUpdating}
      />
      <Button
        type="button"
        label="Guardar"
        icon="pi pi-check"
        loading={isCreating || isUpdating}
        onClick={() => formik.handleSubmit()}
      />
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="p-card p-4">
          <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
            <h2 className="m-0">Categorías del centro de ayuda</h2>
            <Button
              type="button"
              label="Nueva categoría"
              icon="pi pi-plus"
              onClick={openNewDialog}
              loading={isCreating}
            />
          </div>

          <Tooltip
            target=".support-category-description"
            style={{ maxWidth: "24rem" }}
          />

          <DataTable
            value={orderedCategories}
            loading={isLoading || isFetching || isReordering}
            reorderableRows
            onRowReorder={handleRowReorder}
            dataKey="id"
            responsiveLayout="scroll"
            emptyMessage="Todavía no hay categorías. Creá la primera con el botón «Nueva categoría»."
          >
            <Column rowReorder style={{ width: "3rem" }} />
            <Column field="name" header="Nombre" />
            <Column
              field="description"
              header="Descripción"
              body={descriptionBodyTemplate}
              style={{ width: "20rem", maxWidth: "20rem" }}
            />
            {tenantScope.role === "SUPER_ADMIN" && (
              <Column
                header="Tenant"
                body={(category: SupportCategory) => category.tenantProvider || NO_TENANT_LABEL}
                style={{ width: "160px" }}
              />
            )}
            <Column field="icon" header="Ícono" body={iconBodyTemplate} />
            <Column
              field="articleCount"
              header="Artículos"
              style={{ width: "120px" }}
            />
            <Column
              field="isActive"
              header="Activa"
              body={activeBodyTemplate}
              style={{ width: "100px" }}
            />
            <Column
              header="Acciones"
              body={actionsBodyTemplate}
              style={{ width: "180px" }}
            />
          </DataTable>
        </div>
      </div>

      <Dialog
        visible={!!categoryToView}
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-folder-open text-primary"></i>
            <span>Detalle de la categoría</span>
          </div>
        }
        modal
        style={{ width: "42rem", maxWidth: "95vw", maxHeight: "90vh" }}
        breakpoints={{ "960px": "95vw" }}
        contentStyle={{ overflowY: "auto" }}
        onHide={() => setCategoryToView(undefined)}
      >
        {categoryToView && (
          <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-2">
              <h2
                className="text-xl md:text-2xl font-semibold text-900 m-0"
                style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
              >
                {categoryToView.name}
              </h2>
              <div className="flex flex-wrap align-items-center gap-2">
                {categoryToView.isActive ? (
                  <Tag severity="success" value="Activa" />
                ) : (
                  <Tag severity="danger" value="Inactiva" />
                )}
                {tenantScope.role === "SUPER_ADMIN" && (
                  <Tag
                    severity="secondary"
                    value={categoryToView.tenantProvider || NO_TENANT_LABEL}
                  />
                )}
              </div>
            </div>

            <div className="grid m-0">
              <div className="col-12 md:col-6 p-1">
                <div className="surface-card border-1 surface-border border-round p-3 h-full">
                  <small className="text-600 block mb-1">Artículos</small>
                  <span className="text-2xl font-semibold text-900">
                    {categoryToView.articleCount}
                  </span>
                </div>
              </div>
              <div className="col-12 md:col-6 p-1">
                <div className="surface-card border-1 surface-border border-round p-3 h-full">
                  <small className="text-600 block mb-1">Orden</small>
                  <span className="text-2xl font-semibold text-900">
                    {categoryToView.sortOrder}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <div>
                <small className="text-600 block mb-1">Ícono</small>
                <span className="font-medium">{iconBodyTemplate(categoryToView)}</span>
              </div>
              <div>
                <small className="text-600 block">Creada</small>
                <span className="font-medium">{formatDateTime(categoryToView.createdAt)}</span>
              </div>
              <div>
                <small className="text-600 block">Última actualización</small>
                <span className="font-medium">{formatDateTime(categoryToView.updatedAt)}</span>
              </div>
            </div>

            <div>
              <small className="text-600 block mb-2">Descripción</small>
              {categoryToView.description?.trim() ? (
                <div
                  className="p-3 border-1 surface-border border-round surface-card"
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    maxHeight: "22rem",
                    overflow: "auto",
                  }}
                >
                  {categoryToView.description}
                </div>
              ) : (
                <span className="text-500">Sin descripción</span>
              )}
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header={isEdit ? "Editar categoría" : "Nueva categoría"}
        visible={dialogVisible}
        style={{ width: "450px" }}
        modal
        onHide={hideDialog}
        footer={dialogFooter}
      >
        <form onSubmit={formik.handleSubmit} className="formgrid grid">
          <div className="field col-12">
            <label htmlFor="name" className="font-medium text-900">
              Nombre *
            </label>
            <InputText
              id="name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isCreating || isUpdating}
              className={`w-full mt-2 ${formik.touched.name && formik.errors.name ? "p-invalid" : ""
                }`}
              placeholder="Ej: Pagos y facturación"
              autoFocus
            />
            {formik.touched.name && formik.errors.name && (
              <small className="p-error block mt-1">{formik.errors.name}</small>
            )}
          </div>

          <div className="field col-12">
            <label className="font-medium text-900">Ícono</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SUPPORT_ICON_OPTIONS.map((option) => {
                const selected = formik.values.icon === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      formik.setFieldValue("icon", selected ? "" : option.value)
                    }
                    disabled={isCreating || isUpdating}
                    className={`flex flex-column align-items-center justify-content-center gap-1 border-round cursor-pointer p-2 ${selected
                        ? "border-2 border-primary bg-primary-50"
                        : "border-1 surface-border surface-card"
                      }`}
                    style={{ width: "86px", height: "64px" }}
                    title={option.label}
                  >
                    <i
                      className={`${option.primeIcon} text-xl ${selected ? "text-primary" : "text-600"
                        }`}
                    />
                    <span
                      className={`text-xs ${selected ? "text-primary font-medium" : "text-600"}`}
                    >
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <small className="text-600 block mt-1">
              Opcional — tocá de nuevo para quitarlo. Se muestra en la app junto al
              nombre de la categoría.
            </small>
          </div>

          <div className="field col-12">
            <label htmlFor="description" className="font-medium text-900">
              Descripción
            </label>
            <InputText
              id="description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isCreating || isUpdating}
              className={`w-full mt-2 ${formik.touched.description && formik.errors.description ? "p-invalid" : ""
                }`}
              placeholder="Ej: Información sobre reembolsos, pagos fallidos y seguridad transaccional"
            />
            {formik.touched.description && formik.errors.description ? (
              <small className="p-error block mt-1">{formik.errors.description}</small>
            ) : (
              <small className="text-600 block mt-1">
                Opcional — se muestra debajo del nombre en la app (máx. 200)
              </small>
            )}
          </div>

          {!isEdit && !tenantScope.hidden && (
            <div className="field col-12">
              <label htmlFor="tenant_id" className="font-medium text-900">
                Tenant
              </label>
              <Dropdown
                id="tenant_id"
                name="tenant_id"
                value={formik.values.tenant_id}
                onChange={(e) => formik.setFieldValue("tenant_id", e.value ?? NO_TENANT_VALUE)}
                onBlur={formik.handleBlur}
                optionLabel="displayName"
                optionValue="id"
                options={tenantOptions}
                loading={isLoadingTenants}
                disabled={isCreating || isUpdating || tenantScope.locked}
                className="w-full mt-2"
                placeholder="Seleccione un tenant (opcional)"
              />
              {tenantScope.locked && (
                <small className="text-600 block mt-1">
                  Fijado por el tenant seleccionado en la barra superior.
                </small>
              )}
            </div>
          )}
        </form>
      </Dialog>

      <Dialog
        header="Eliminar categoría"
        visible={!!categoryToDelete}
        style={{ width: "450px" }}
        modal
        onHide={() => setCategoryToDelete(undefined)}
        footer={
          <div>
            <Button
              type="button"
              label="Cancelar"
              icon="pi pi-times"
              severity="secondary"
              onClick={() => setCategoryToDelete(undefined)}
              disabled={isDeleting}
            />
            <Button
              type="button"
              label="Eliminar"
              icon="pi pi-trash"
              severity="danger"
              loading={isDeleting}
              onClick={handleDelete}
            />
          </div>
        }
      >
        {categoryToDelete && (
          <>
            <h3 className="text-red-500 mt-0">
              <i className="pi pi-exclamation-triangle"></i> ¡Cuidado!
            </h3>
            <p className="m-0">
              ¿Estás seguro que querés eliminar la categoría{" "}
              <b>{categoryToDelete.name}</b>?
            </p>
            <p className="text-orange-600 mt-2 mb-0">
              <i className="pi pi-info-circle"></i> Se van a eliminar también sus{" "}
              <b>{categoryToDelete.articleCount}</b> artículos asociados. Esta
              acción no se puede deshacer.
            </p>
          </>
        )}
      </Dialog>
    </div>
  );
}
